import React, { useEffect } from 'react';
import { useNotifications } from 'hooks/useNotifications';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import './NotificationContainer.css';

const NotificationContainer: React.FC = () => {
  const { notifications, hideNotification } = useNotifications();

  useEffect(() => {
    notifications.forEach((notification) => {
      if (notification.duration) {
        const timer = setTimeout(() => {
          hideNotification(notification.id);
        }, notification.duration);

        return () => clearTimeout(timer);
      }
    });
  }, [notifications, hideNotification]);

  if (notifications.length === 0) return null;

  const getNotificationStyles = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-success-50 border-l-4 border-success-500 text-success-800';
      case 'error':
        return 'bg-error-50 border-l-4 border-error-500 text-error-800';
      case 'warning':
        return 'bg-warning-50 border-l-4 border-warning-500 text-warning-800';
      case 'info':
        return 'bg-primary-50 border-l-4 border-primary-500 text-primary-800';
      default:
        return 'bg-gray-50 border-l-4 border-gray-500 text-gray-800';
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'text-success-500';
      case 'error':
        return 'text-error-500';
      case 'warning':
        return 'text-warning-500';
      case 'info':
        return 'text-primary-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="fixed top-5 right-5 z-50 flex flex-col gap-3 max-w-sm">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`flex items-center justify-between p-4 rounded-lg shadow-medium animate-slide-in ${getNotificationStyles(notification.type)}`}
        >
          <div className="flex items-center gap-3">
            <div className={`flex-shrink-0 ${getIconColor(notification.type)}`}>
              {notification.type === 'success' && <CheckCircle size={20} />}
              {notification.type === 'error' && <XCircle size={20} />}
              {notification.type === 'warning' && <AlertTriangle size={20} />}
              {notification.type === 'info' && <Info size={20} />}
            </div>
            <span className="text-sm font-medium leading-relaxed">
              {notification.message}
            </span>
          </div>
          <button
            className="flex-shrink-0 ml-4 p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors"
            onClick={() => hideNotification(notification.id)}
          >
            <X size={16} className="opacity-70" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default NotificationContainer;